import { useCallback, useRef, useEffect } from 'react';
import RestaurantTablePlanVisual from './RestaurantTablePlanVisual';
import {
  PLAN_LOGICAL_H,
  PLAN_LOGICAL_W,
  borderRadiusForShape,
  clampPlanPosition,
  logicalToPercent,
  pointerExceededThreshold,
  resolveTableLayout,
  snapToGrid,
  DRAG_THRESHOLD_PX,
  MIN_TABLE_SIZE,
  MAX_TABLE_SIZE,
} from '../../utils/restaurantFloorPlan';

function tableStatusMeta(status, hasOpenTicketHint, hasResa) {
  if (status === 'OCCUPIED') {
    return {
      label: hasOpenTicketHint ? 'En service' : 'En service',
      badge: 'bg-amber-100 text-amber-950 border-amber-300',
      dot: 'bg-amber-500',
    };
  }
  if (hasResa) {
    return {
      label: 'Réservée',
      badge: 'bg-violet-50 text-violet-950 border-violet-300',
      dot: 'bg-violet-500',
    };
  }
  return {
    label: 'Libre',
    badge: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    dot: 'bg-emerald-500',
  };
}

function capacityLine(row) {
  const cap = row.table?.capacity;
  const capLabel = cap != null ? `${cap} place${cap > 1 ? 's' : ''}` : '— places';
  const covers = row.openSession?.coverCount;
  if (row.operationalStatus === 'OCCUPIED' && covers != null) {
    const over = cap != null && covers > cap;
    return {
      text: `${covers} / ${capLabel.replace(' places', '')} places`,
      over,
    };
  }
  return { text: capLabel, over: false };
}

/**
 * @param {object} props
 * @param {'plan'|'config'} props.mode
 */
export default function RestaurantFloorPlanCanvas({
  mode,
  rows,
  selectedTableId,
  onSelectConfigTable,
  onOpenServiceTable,
  onLocalTablePatch,
  onPersistTableLayout,
  resaByTableId = {},
  moduleResaOk,
  formatResaTime,
  formatElapsed,
}) {
  const rootRef = useRef(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const gestureRef = useRef({
    active: false,
    pointerId: null,
    kind: null,
    tableId: null,
    startClientX: 0,
    startClientY: 0,
    moved: false,
    suppressClick: false,
    origX: 0,
    origY: 0,
    origW: 0,
    origH: 0,
  });

  const finishGesture = useCallback(
    (row) => {
      const g = gestureRef.current;
      if (!g.active || !row) return;
      g.active = false;
      g.suppressClick = g.moved;
      g.pointerId = null;
      if (g.moved && mode === 'config') {
        const layout = resolveTableLayout(row.table);
        onPersistTableLayout(row.table.id, {
          planX: layout.x,
          planY: layout.y,
          planWidth: layout.width,
          planHeight: layout.height,
        });
      }
    },
    [mode, onPersistTableLayout]
  );

  const handlePointerMove = useCallback(
    (e) => {
      const g = gestureRef.current;
      if (!g.active || g.pointerId !== e.pointerId) return;
      if (!pointerExceededThreshold(g.startClientX, g.startClientY, e.clientX, e.clientY, DRAG_THRESHOLD_PX)) {
        return;
      }
      g.moved = true;
      const root = rootRef.current;
      const scaleX = PLAN_LOGICAL_W / (root?.clientWidth || PLAN_LOGICAL_W);
      const scaleY = PLAN_LOGICAL_H / (root?.clientHeight || PLAN_LOGICAL_H);
      const dx = (e.clientX - g.startClientX) * scaleX;
      const dy = (e.clientY - g.startClientY) * scaleY;

      if (g.kind === 'move') {
        const pos = clampPlanPosition(
          snapToGrid(g.origX + dx),
          snapToGrid(g.origY + dy),
          g.origW,
          g.origH
        );
        onLocalTablePatch(g.tableId, { planX: pos.x, planY: pos.y });
      } else if (g.kind === 'resize') {
        let nw = snapToGrid(g.origW + dx);
        let nh = snapToGrid(g.origH + dy);
        nw = Math.min(MAX_TABLE_SIZE, Math.max(MIN_TABLE_SIZE, nw));
        nh = Math.min(MAX_TABLE_SIZE, Math.max(MIN_TABLE_SIZE, nh));
        const row = rowsRef.current.find((r) => r.table.id === g.tableId);
        const shape = resolveTableLayout(row?.table || {}).shape;
        if (shape === 'ROUND' || shape === 'SQUARE') {
          const side = Math.max(nw, nh);
          nw = side;
          nh = side;
        }
        onLocalTablePatch(g.tableId, { planWidth: nw, planHeight: nh });
      }
    },
    [onLocalTablePatch]
  );

  const endPointerGesture = useCallback(
    (e) => {
      const g = gestureRef.current;
      if (!g.active || g.pointerId !== e.pointerId) return;
      try {
        rootRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const row = rowsRef.current.find((r) => r.table.id === g.tableId);
      finishGesture(row);
    },
    [finishGesture]
  );

  useEffect(() => {
    if (mode !== 'config') return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    root.addEventListener('pointermove', handlePointerMove);
    root.addEventListener('pointerup', endPointerGesture);
    root.addEventListener('pointercancel', endPointerGesture);
    return () => {
      root.removeEventListener('pointermove', handlePointerMove);
      root.removeEventListener('pointerup', endPointerGesture);
      root.removeEventListener('pointercancel', endPointerGesture);
    };
  }, [mode, handlePointerMove, endPointerGesture]);

  const handlePointerDown = (e, row, kind = 'move') => {
    if (mode !== 'config') return;
    if (e.button != null && e.button !== 0) return;
    const layout = resolveTableLayout(row.table);
    gestureRef.current = {
      active: true,
      pointerId: e.pointerId,
      kind,
      tableId: row.table.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moved: false,
      suppressClick: false,
      origX: layout.x,
      origY: layout.y,
      origW: layout.width,
      origH: layout.height,
    };
    rootRef.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
    onSelectConfigTable(row);
  };

  const handleTableClick = (e, row) => {
    if (gestureRef.current.suppressClick) {
      gestureRef.current.suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (mode === 'config') {
      onSelectConfigTable(row);
      return;
    }
    onOpenServiceTable(row);
  };

  return (
    <div
      className="relative bg-white rounded-2xl border border-slate-200 mx-auto overflow-hidden touch-none select-none"
      style={{ width: '100%', maxWidth: PLAN_LOGICAL_W, aspectRatio: `${PLAN_LOGICAL_W} / ${PLAN_LOGICAL_H}` }}
      ref={rootRef}
      data-testid="restaurant-floor-plan"
      data-floor-plan-root
    >
      {rows.map((row) => {
        const layout = resolveTableLayout(row.table);
        const pct = logicalToPercent(layout.x, layout.y, layout.width, layout.height);
        const resaList = resaByTableId[row.table.id] || [];
        const meta = tableStatusMeta(
          row.operationalStatus,
          (row.openSession?.additionsCount || 0) > 0,
          moduleResaOk && resaList.length > 0 && row.operationalStatus !== 'OCCUPIED'
        );
        const cap = capacityLine(row);
        const selected = selectedTableId === row.table.id;
        const transform = layout.rotation === 90 ? 'rotate(90deg)' : undefined;
        return (
          <div
            key={row.table.id}
            role="button"
            tabIndex={0}
            data-testid={`restaurant-table-${row.table.label}`}
            data-operational-status={row.operationalStatus}
            className={`absolute border-2 overflow-hidden shadow-md ${meta.badge} ${
              mode === 'config' ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
            } ${
              selected && mode === 'config' ? 'ring-2 ring-emerald-500 ring-offset-1' : ''
            }`}
            style={{
              ...pct,
              borderRadius: borderRadiusForShape(layout.shape),
              transform,
              transformOrigin: 'center center',
            }}
            onClick={(e) => handleTableClick(e, row)}
            onPointerDown={(e) => handlePointerDown(e, row, 'move')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleTableClick(e, row);
            }}
          >
            <RestaurantTablePlanVisual
              row={row}
              layout={layout}
              meta={meta}
              cap={cap}
              mode={mode}
              moduleResaOk={moduleResaOk}
              resaList={resaList}
              formatResaTime={formatResaTime}
              formatElapsed={formatElapsed}
            />
            {mode === 'config' && selected && (
              <span
                role="presentation"
                data-testid={`restaurant-table-resize-${row.table.label}`}
                className="absolute bottom-0 right-0 z-30 flex h-7 w-7 cursor-se-resize touch-manipulation items-end justify-end pointer-events-auto"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handlePointerDown(e, row, 'resize');
                }}
                aria-label="Redimensionner la table"
              >
                <span
                  className="mb-px mr-px h-3 w-3 rounded-tl-[2px] border border-white/80 bg-slate-500/45 shadow-sm"
                  aria-hidden
                />
              </span>
            )}
          </div>
        );
      })}
      {rows.length === 0 && (
        <p className="p-8 text-center text-slate-500">Aucune table — créez-en dans Configuration.</p>
      )}
    </div>
  );
}
